import type { LoginResponse, UserDto } from '@gestao/shared'
import { create } from 'zustand'

/**
 * Sessão do usuário. O access token vive só em memória (nunca em storage); o refresh token é
 * um cookie httpOnly que o navegador manda sozinho para /api/v1/auth/*.
 *
 * - `unknown`: o app ainda não tentou restaurar a sessão (primeira carga).
 * - `authenticated`: há token e usuário.
 * - `anonymous`: sem sessão; `endReason` diz por quê (para o login avisar ou não).
 */
export type SessionStatus = 'unknown' | 'authenticated' | 'anonymous'
export type SessionEndReason = 'expired' | 'signed-out' | 'required'

export interface SessionState {
  status: SessionStatus
  accessToken: string | null
  user: UserDto | null
  endReason: SessionEndReason | null
}

const initialState: SessionState = {
  status: 'unknown',
  accessToken: null,
  user: null,
  endReason: null,
}

export const useSessionStore = create<SessionState>()(() => initialState)

/** Ações da sessão (fora de componentes também: api.ts, sincronização entre abas). */
export const session = {
  get: () => useSessionStore.getState(),

  signIn(response: LoginResponse): void {
    useSessionStore.setState({
      status: 'authenticated',
      accessToken: response.accessToken,
      user: response.user,
      endReason: null,
    })
  },

  setUser(user: UserDto): void {
    if (useSessionStore.getState().status !== 'authenticated') return
    useSessionStore.setState({ user })
  },

  /** Marca a troca de senha como obrigatória (a API respondeu PASSWORD_CHANGE_REQUIRED). */
  requirePasswordChange(): void {
    const { user } = useSessionStore.getState()
    if (user && !user.mustChangePassword) {
      useSessionStore.setState({ user: { ...user, mustChangePassword: true } })
    }
  },

  end(reason: SessionEndReason): void {
    useSessionStore.setState({
      status: 'anonymous',
      accessToken: null,
      user: null,
      endReason: reason,
    })
  },

  /** Só para testes. */
  reset(): void {
    useSessionStore.setState(initialState, true)
  },
}

/** Usuário autenticado; use dentro das rotas protegidas (o guard garante que existe). */
export function useCurrentUser(): UserDto {
  const user = useSessionStore((state) => state.user)
  if (!user) throw new Error('useCurrentUser fora de uma rota autenticada.')
  return user
}
